import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Grid,
  styled,
  Fab,
  Chip,
  Button,
  Stack,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import LandingPageNav from '../../components/landingpage/LandingPageNav';
import Footer from '../../components/Footer/footer';
import { apigetPublic, apidelete } from '../../service/api';


const DashboardContainer = styled(Container)(() => ({
  paddingTop: '100px',
  paddingBottom: '50px',
}));

const IntakeCard = styled(Card)(({ theme }) => ({
  height: '100%',
  boxShadow: theme.shadows[3],
  borderRadius: '16px',
  overflow: 'hidden',
  transition: 'transform 0.3s, box-shadow 0.3s',
  '&:hover': {
    transform: 'translateY(-6px)',
    boxShadow: theme.shadows[6],
  },
}));

const IntakeLink = styled(Link)(() => ({
  textDecoration: 'none',
  color: 'inherit',
}));

const IntakeImage = styled(CardMedia)(() => ({
  height: 160,
  backgroundSize: 'contain',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  backgroundColor: '#f5f5f5',
}));

export default function BuilderIntakeDashboard() {
  const [intakes, setIntakes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  const fetchIntakes = async () => {
    const result = await apigetPublic('intake');
    if (result?.status === 200 && Array.isArray(result.data?.result)) {
      setIntakes(result.data.result);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchIntakes();
  }, []);

  const normalizeUrl = (url) => {
    if (!url) return '/assets/images/no-image.jpg';
    return url.startsWith('http')
      ? url
      : `https://${url.replace(/^https?:\/\//, '')}`;
  };

  const handleDelete = async (e, intakeId) => {
    e.preventDefault();
    e.stopPropagation();

    if (!window.confirm('Delete this intake form?')) return;

    try {
      const res = await apidelete(`intake/${intakeId}`);
      if (res?.status === 200) {
        toast.success('Intake deleted');
        fetchIntakes();
      }
    } catch (err) {
      toast.error('Failed to delete intake');
    }
  };

  return (
    <>
      <LandingPageNav />

      <Box
        sx={{
          backgroundImage: 'url(/assets/backgroundimageforherobanner.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          minHeight: '100vh',
          pt: 5,
        }}
      >
        <Box
          sx={{
            backgroundColor: 'rgba(255,255,255,0.9)',
            minHeight: '100vh',
            position: 'relative',
          }}
        >
          <DashboardContainer>
            <Typography variant="h4" gutterBottom sx={{ textAlign: 'center', mb: 4 }}>
              Builder Intake Dashboard
            </Typography>

            {isLoading ? (
              <Typography align="center">Loading intake forms...</Typography>
            ) : intakes.length > 0 ? (
              <Grid container spacing={4}>
                {intakes.map((intake) => (
                  <Grid item xs={12} sm={6} md={4} key={intake._id}>
                    <IntakeLink to={`/dashboard/intake/${intake._id}`}>
                      <IntakeCard>
                        <IntakeImage image={normalizeUrl(intake.logoSrc)} />

                        <CardContent>
                          <Typography variant="h6" sx={{ mb: 1 }}>
                            {intake.title}
                          </Typography>

                          <Typography variant="body2" color="text.secondary">
                            Slug: {intake.slug}
                          </Typography>

                          <Typography variant="body2" color="text.secondary">
                            Loan Officers: {intake.loanOfficers?.length || 0}
                          </Typography>

                          <Typography variant="body2" color="text.secondary">
                            Agents: {intake.agentNames?.length || 0}
                          </Typography>

                          <Typography variant="body2" color="text.secondary">
                            Communities: {intake.communities?.length || 0}
                          </Typography>

                          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                            <Chip
                              size="small"
                              label={intake.isActive ? 'Active' : 'Inactive'}
                              color={intake.isActive ? 'success' : 'default'}
                            />

                            <Button
                              size="small"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                navigate(`/dashboard/intake/${intake._id}`);
                              }}
                            >
                              Edit
                            </Button>

                            <Button
                              size="small"
                              color="error"
                              onClick={(e) => handleDelete(e, intake._id)}
                            >
                              Delete
                            </Button>
                          </Stack>
                        </CardContent>
                      </IntakeCard>
                    </IntakeLink>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography align="center" sx={{ mt: 4 }}>
                No builder intake forms found.
              </Typography>
            )}
          </DashboardContainer>

          <Fab
            color="primary"
            aria-label="add"
            onClick={() => navigate('/dashboard/intake/create')}
            sx={{
              position: 'fixed',
              bottom: 32,
              right: 32,
              backgroundColor: '#1976d2',
              '&:hover': { backgroundColor: '#115293' },
            }}
          >
            <AddIcon />
          </Fab>
        </Box>
      </Box>

      <Box sx={(theme) => ({ ...theme.mixins.toolbar })} />
      <Footer />
    </>
  );
}
